import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { completeProfilePath, rememberCampaignIdFromSearch, resolveCampaignId } from '../../lib/campaignRedirect';
import { AppRole, defaultPathForRole } from '../../lib/roles';
import PageSpinner from '../common/PageSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: AppRole[];
}

export default function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, loading, user, hasRole } = useAuth();

  React.useEffect(() => {
    rememberCampaignIdFromSearch(location.search);
  }, [location.search]);

  if (loading) {
    return <PageSpinner message="Checking your session..." />;
  }

  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/signin${location.search}`} replace state={{ from: returnTo }} />;
  }

  if (user?.availableRoles.some((role) => role === 'orgAdmin' || role === 'orgManager' || role === 'employee') && user.profileComplete === false && location.pathname !== '/complete-profile') {
    const campaignId = resolveCampaignId(location.search);
    return <Navigate to={completeProfilePath(campaignId)} replace />;
  }

  if (user?.availableRoles.length && user.availableRoles.length > 1 && !user.activeRole) {
    return <Navigate to="/choose-role" replace />;
  }

  if (roles && !hasRole(roles)) {
    return <Navigate to={defaultPathForRole(user?.role ?? null)} replace />;
  }

  return <>{children}</>;
}
